import { ExportParentableTypes, ExportStatus } from '@/gql/graphql';
import {
  Button,
  Collapse,
  Divider,
  Flex,
  HStack,
  Progress,
  Tag,
  Text,
  VStack,
  Icon,
} from '@chakra-ui/react';
import { FiExternalLink } from 'react-icons/fi';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useExportItem } from '../hooks/useExportItem';
import { FileTransferItem, KVItem, KVTable } from './FileTransferItem';

function Body() {
  const { t } = useTranslation();
  const {
    exportItem: { exportType, titleEvent, author },
  } = useExportItem();

  return (
    <KVTable>
      <KVItem label={t('file_transfer.file_type')} value={`.${exportType}`} />
      <KVItem label={t('file_transfer.requested_by')} value={author?.fullName || ''} />
      {!!titleEvent && <KVItem label={t('file_transfer.event')} value={titleEvent || ''} />}
    </KVTable>
  );
}

function Footer() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = React.useState(true);
  const {
    exportItem: {
      progress,
      status,
      url,
      pivoted,
      exportErrors,
      exportColumns,
      dateEnd,
      dateStart,
      exportType,
      parentableId,
      parentableType,
    },
  } = useExportItem();

  return (
    <VStack w="full">
      <HStack w="full" px="2" py="1">
        <Flex gap="2" flex={1}>
          <Button
            disabled={status !== ExportStatus.Done}
            colorScheme={status === ExportStatus.Done ? 'primary' : 'gray'}
            onClick={() => {
              window.open(`${url}`);
            }}
            size="sm"
            variant="outline"
          >
            {t('file_transfer.download')}
          </Button>
          <Button
            onClick={() => setCollapsed((old) => !old)}
            size="sm"
            variant="outline"
          >
            {t('file_transfer.details')}
          </Button>
          {exportType === 'pdf' &&
            parentableType === ExportParentableTypes.Events &&
            !!parentableId && (
              <Button
                size="sm"
                variant="outline"
                rightIcon={<Icon as={FiExternalLink} />}
                onClick={() =>
                  window.open(`/external/event/${parentableId}`, '_blank')
                }
              >
                {t('common.view_event')}
              </Button>
            )}
        </Flex>
        <Flex flex="0.5">
          {[ExportStatus.Processing, ExportStatus.Pending].includes(
            status!,
          ) && (
            <Progress
              borderWidth="thin"
              w="full"
              size="lg"
              rounded="md"
              value={progress || 0}
              isIndeterminate={status === ExportStatus.Pending}
            />
          )}
        </Flex>
      </HStack>
      <Collapse style={{ width: '100%' }} in={!collapsed}>
        <VStack
          alignItems="flex-start"
          w="full"
          p="2"
          bg="white"
          rounded="lg"
          borderWidth="thin"
        >
          <KVTable>
            {!!exportErrors?.length && (
              <KVItem
                label={t('file_transfer.errors')}
                value={
                  <>
                    {exportErrors?.map((err) => (
                      <Text fontWeight="bold" color="red" key={err} as="span">
                        {err}
                      </Text>
                    ))}
                  </>
                }
              />
            )}
            <KVItem label={t('file_transfer.pivoted')} value={pivoted ? t('file_transfer.yes') : t('file_transfer.no')} />
            {!!dateStart && (
              <KVItem
                label={t('file_transfer.start_date')}
                value={`${moment(dateStart).format('L')}`}
              />
            )}
            {!!dateEnd && (
              <KVItem
                label={t('file_transfer.end_date')}
                value={`${moment(dateEnd).format('L')}`}
              />
            )}
            {!!exportColumns?.length && (
              <KVItem label={t('file_transfer.columns')} value={exportColumns?.join(', ') || ''} />
            )}

          </KVTable>
        </VStack>
      </Collapse>
    </VStack>
  );
}

export function ExportItem() {
  const { t } = useTranslation();
  const { exportItem } = useExportItem();

  const { parentableType, createdAt, status } = exportItem;

  const title = React.useMemo(() => {
    switch (parentableType) {
      case ExportParentableTypes.Collections:
        return t('file_transfer.export_types.collections');
      case ExportParentableTypes.Dashboard:
        return t('file_transfer.export_types.dashboard');
      case ExportParentableTypes.Events:
        return t('file_transfer.export_types.events');
      case ExportParentableTypes.Formularies:
        return t('file_transfer.export_types.formularies');
      case ExportParentableTypes.Locals:
        return t('file_transfer.export_types.locals');
      case ExportParentableTypes.Schedules:
        return t('file_transfer.export_types.schedules');
      case ExportParentableTypes.TaskAnswers:
        return t('file_transfer.export_types.task_answers');
      case ExportParentableTypes.Tasks:
        return t('file_transfer.export_types.tasks');
      case ExportParentableTypes.Tickets:
        return t('file_transfer.export_types.tickets');
      case ExportParentableTypes.Users:
        return t('file_transfer.export_types.users');
      case ExportParentableTypes.PhotoBook:
        return t('file_transfer.export_types.photo_book');

      default:
        return '';
    }
  }, [parentableType, t]);

  const parsedDate = moment(createdAt).format('LLLL');

  return (
    <FileTransferItem
      title={title}
      subtitle={parsedDate}
      status={status || 'pending'}
      body={<Body />}
      footer={<Footer />}
    />
  );
}
